import { observable, configure, action, computed } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';
import { buildHourIndex, lookupHourEvents } from '../lib/calendarHourIndex';
import { assignOverlaps } from '../lib/calendarOverlap';

configure({ enforceActions: 'always' });

function formatDateToICal(dateString) {
    const date = new Date(dateString);
    const yyyyMMdd = date.toISOString().slice(0, 10).replace(/-/g, '');
    const HHmmss = date.toISOString().slice(11, 19).replace(/:/g, '');
    return `${yyyyMMdd}T${HHmmss}Z`;
}

function escapeICalText(text) {
    return text
      .replace(/\\/g, '\\\\') // Escape backslashes
      .replace(/;/g, '\\;')   // Escape semicolons
      .replace(/,/g, '\\,')   // Escape commas
      .replace(/\n/g, '\\n'); // Replace newlines with literal \n
}

function foldLine(line) {
    const maxLength = 75;
    let result = '';
    while (line.length > maxLength) {
      result += line.slice(0, maxLength) + '\r\n ';
      line = line.slice(maxLength);
    }
    result += line;
    return result;
}

function generateICalContent(eventData) {
    const {
        uuidv4,
        createdDate,
        start,
        end,
        title,
        body,
        calendarId,
        animals,
        customer
    } = eventData;

    const dtstamp = formatDateToICal(createdDate || new Date());
    const dtstart = formatDateToICal(start);
    const dtend = formatDateToICal(end);
    const uid = uuidv4 || `${eventData.id}@yourdomain.com`;
    const summary = title || 'No Title';
    const descriptionLines = [];

    descriptionLines.push(body || 'No Description');
    if (animals && animals.length > 0) {
        descriptionLines.push(`Animal: ${animals[0].name}`);
    }
    descriptionLines.push(`Customer ID: ${customer}`);
    const description = escapeICalText(descriptionLines.join('\n'));

    return `
BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Dyrejournal//Dyrejournal//EN
BEGIN:VEVENT
UID:${uid}
DTSTAMP:${dtstamp}
DTSTART:${dtstart}
DTEND:${dtend}
SUMMARY:${summary}
DESCRIPTION:${description}
ORGANIZER;CN=${calendarId}:MAILTO:no-reply@yourdomain.com
END:VEVENT
END:VCALENDAR
`.trim();
}

class CalendarEventStore extends LocalModel {
    constructor() {
        super('calendarEvent', {
            namePlural: 'calendarEvents',
            namePluralReal: 'calendarEvents',
            sort: '-createdDate',
            limit: 500,
            api: {
                search: {
                    url: '/api/calendarevents/',
                    params: {
                        limit: 500,
                        sort: '-createdDate',
                    },
                },
                load: {
                    url: '/api/calendarevents/',
                    params: {},
                },
                save: {
                    url: '/api/calendarevents/',
                    params: {},
                },
                delete: {
                    url: '/api/calendarevents/',
                    params: {},
                },
            },
        });
    }

    @observable newCalendarEvent = {};

    @observable calendarEvent = {};

    @observable calendarEvents = [];

    @observable calendars = [];

    @observable animals = [];

    @observable visitors = [];

    @observable clientHours = [];

    @observable adminAnimals = [];

    @observable totalHours = [];

    @observable animalBookings = [];

    @observable recurringList = [];

    @observable creators = [];

    @observable currentTime = null;

    @observable currentTimeOffset = null;

    @observable href = null;

    @observable icsContent = null;

    @observable calendarEventNotifications = [];

    @observable noShows = [];

    @observable unconfirmedBookings = [];

    @observable unconfirmedBookingsTotal = 0;

    @observable noShowSummary = [];

    @observable noShowTotal = 0;

    @observable notificationCalendars = [];

    @observable notificationTotal = 0;

    @observable reminderLog = [];

    @observable reminderLogMeta = {};

    @observable missingReminders = [];

    @observable missingRemindersTotal = 0;

    @observable missingRemindersScanned = 0;

    // Hour-bucket indexes so getEvents is a lookup instead of a full scan per
    // hour-cell. Rebuilt automatically whenever the arrays are replaced via
    // updateKeyValue (loads, excludeDate filtering) or an event's start changes.
    @computed get calendarEventsByHour() {
        return buildHourIndex(this.calendarEvents);
    }

    @computed get recurringListByHour() {
        return buildHourIndex(this.recurringList);
    }

    getEvents(date, calendarId) {
        const windowStartEpoch = new Date(date).getTime();

        const allCalendarIds = new Set(['__all__']);
        if (util.isArray(calendarId)) {
            calendarId.forEach(id => allCalendarIds.add(id));
        } else {
            allCalendarIds.add(calendarId);
        }

        const events = lookupHourEvents(this.calendarEventsByHour, windowStartEpoch, allCalendarIds);

        const { recurringList } = this;
        if (recurringList && recurringList.length > 0) {
            events.push(...lookupHourEvents(this.recurringListByHour, windowStartEpoch, allCalendarIds));
        }
        return events;
    }

    @action
    isOverlapping() {
        // Combine regular events and recurring events for overlap detection.
        assignOverlaps([...this.calendarEvents, ...(this.recurringList || [])]);
    }

    async sendSMS(id, text) {
        const response = await util.fetchApi(`/api/calendarevents/${id}/sms`, { method: 'GET' }, { text });
        switch (response.status) {
            case 200:
                return response;
            default:
                return 'Error';
        }
    }

    // Kalender-SMS (Dyrejournal): the appointments of one day as SMS candidates.
    // Backend: routes/api/calendarevents/post-calendarevent-bulk-sms-preview.js
    async bulkSmsPreview({ calendarDate, calendarIds = [] } = {}) {
        return util.fetchApi('/api/calendarevents/bulk-sms/preview', { method: 'POST' }, { calendarDate, calendarIds });
    }

    // Kalender-SMS: queue one utsendelse job for the ticked appointments.
    // Backend: routes/api/calendarevents/post-calendarevent-bulk-sms.js
    async bulkSmsCreate({
        calendarDate, calendarIds = [], eventIds = [], body = '',
    } = {}) {
        return util.fetchApi('/api/calendarevents/bulk-sms', { method: 'POST' }, {
            calendarDate, calendarIds, eventIds, body,
        });
    }

    async importCsvEvents(events, calendarId, replaceImportId) {
        const body = { events, calendarId };
        if (replaceImportId) {
            body.replaceImportId = replaceImportId;
        }
        const response = await util.fetchApi('/api/calendarevents/csv/import', { method: 'POST' }, body);
        return response;
    }

    @action
    handleGenerateICS(eventData) {
        if (this.href) {
            URL.revokeObjectURL(this.href);
        }
        const icsContent = generateICalContent(eventData);
        this.icsContent = icsContent;
        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        this.href = url;
    }

    @action
    async loadNotifications(filters = {}) {
        const params = {};
        if (filters.search) params.search = filters.search;
        if (filters.calendarId) params.calendarId = filters.calendarId;
        if (filters.status) params.status = filters.status;
        if (filters.species) params.species = filters.species;
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
        if (filters.includePast) params.includePast = true;
        if (filters.limit) params.limit = filters.limit;
        if (filters.skip) params.skip = filters.skip;

        const response = await util.fetchApi('/api/calendarevents/notifications', { method: 'GET' }, params);
        if (response && response.status === 200) {
            this.updateKeyValue('calendarEventNotifications', response.data || []);
            this.updateKeyValue('notificationCalendars', response.calendars || []);
            this.updateKeyValue('notificationTotal', response.total || 0);
        }
        return response;
    }

    // Dyrejournal «Ubekreftede timer»: past bookings that never became a sale.
    // Dates are ISO strings; the backend defaults to the last 90 days and never
    // looks past now.
    @action
    async loadUnconfirmedBookings({ start, end, search, limit } = {}) {
        const params = {};
        if (start) params.start = start;
        if (end) params.end = end;
        if (search) params.search = search;
        if (limit) params.limit = limit;

        const response = await util.fetchApi('/api/dyrejournal/unconfirmed-bookings', { method: 'GET' }, params);
        if (response && response.status === 200) {
            this.updateKeyValue('unconfirmedBookings', response.data || []);
            this.updateKeyValue('unconfirmedBookingsTotal', response.total || 0);
        }
        return response;
    }

    // Dyrejournal «Ikke møtt» overview. Dates are ISO strings; the backend
    // defaults to the last twelve months when they are missing or invalid.
    @action
    async loadNoShows({ start, end, search, limit } = {}) {
        const params = {};
        if (start) params.start = start;
        if (end) params.end = end;
        if (search) params.search = search;
        if (limit) params.limit = limit;

        const response = await util.fetchApi('/api/dyrejournal/no-shows', { method: 'GET' }, params);
        if (response && response.status === 200) {
            this.updateKeyValue('noShows', response.data || []);
            this.updateKeyValue('noShowSummary', response.summary || []);
            this.updateKeyValue('noShowTotal', response.total || 0);
        }
        return response;
    }

    @action
    async loadReminderLog({
        startDate, endDate, calendarId, missingOnly, reminderType, page, limit,
    } = {}) {
        const params = {};
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        if (calendarId) params.calendarId = calendarId;
        if (missingOnly) params.missingOnly = 1;
        if (reminderType) params.reminderType = reminderType;
        if (page) params.page = page;
        if (limit) params.limit = limit;
        const response = await util.fetchApi('/api/calendarevents/reminder-log',
            { method: 'GET' }, params);
        this.updateKeyValue('reminderLog', response?.data || []);
        this.updateKeyValue('reminderLogMeta', response?.meta || {});
        return response;
    }

    @action
    async loadMissingReminders(filters = {}) {
        const params = {};
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
        if (filters.search) params.search = filters.search;
        if (filters.species) params.species = filters.species;
        if (filters.productName) params.productName = filters.productName;
        if (filters.limit) params.limit = filters.limit;
        if (filters.skip) params.skip = filters.skip;

        const response = await util.fetchApi('/api/calendarevents/missing-reminders',
            { method: 'GET' }, params);
        if (response && response.status === 200) {
            this.updateKeyValue('missingReminders', response.data || []);
            this.updateKeyValue('missingRemindersTotal', response.total || 0);
            this.updateKeyValue('missingRemindersScanned', response.scanned || 0);
        }
        return response;
    }

    @action
    async createMissingRemindersBatch(animalIds = []) {
        if (!animalIds || animalIds.length === 0) return null;
        const response = await util.fetchApi('/api/calendarevents/missing-reminders/batch',
            { method: 'POST' }, { animalIds });
        return response;
    }

    @action
    async createQuickNotification({ animalId, productId, days } = {}) {
        const body = { animalId };
        if (productId) body.productId = productId;
        if (days) body.days = days;
        const response = await util.fetchApi('/api/calendarevents/quick-notification',
            { method: 'POST' }, body);
        return response;
    }

    @action
    async createMissingReminder({ animalId, saleId, reminderDate } = {}) {
        const body = { animalId };
        if (saleId) body.saleId = saleId;
        if (reminderDate) body.reminderDate = reminderDate;
        const response = await util.fetchApi('/api/calendarevents/missing-reminders',
            { method: 'POST' }, body);
        // Both 200 (created) and 409 (already exists) mean the row should disappear
        // from the missing list — they're covered now.
        if (response && (response.status === 200 || response.status === 409)) {
            const next = this.missingReminders.filter(r => r.animal?.id !== animalId);
            this.updateKeyValue('missingReminders', next);
            this.updateKeyValue('missingRemindersTotal', Math.max(0, this.missingRemindersTotal - 1));
        }
        return response;
    }

    @action
    async rescheduleNotification(id, newStartDate) {
        const startObj = new Date(newStartDate);
        const existing = this.calendarEventNotifications.find(e => e.id === id);
        if (existing && existing.start) {
            const prev = new Date(existing.start);
            startObj.setHours(prev.getHours(), prev.getMinutes(), prev.getSeconds(), 0);
        } else {
            startObj.setHours(12, 0, 0, 0);
        }

        let endObj;
        if (existing && existing.start && existing.end) {
            const durationMs = new Date(existing.end).getTime() - new Date(existing.start).getTime();
            endObj = new Date(startObj.getTime() + durationMs);
        } else {
            endObj = new Date(startObj.getTime() + 60 * 60 * 1000);
        }

        const body = {
            start: startObj.toISOString(),
            end: endObj.toISOString(),
        };

        const response = await util.fetchApi(`/api/calendarevents/${id}`, { method: 'PATCH' }, body);
        if (response && (response.status === 200 || response.status === 202)) {
            const updated = (response.data && response.data.calendarevent) || null;
            const newList = this.calendarEventNotifications.map(e => {
                if (e.id !== id) return e;
                return {
                    ...e,
                    start: updated && updated.start ? updated.start : startObj.toISOString(),
                    end: updated && updated.end ? updated.end : endObj.toISOString(),
                    notificationSMSSent: null,
                };
            });
            this.updateKeyValue('calendarEventNotifications', newList);
        }
        return response;
    }

}

const store = new CalendarEventStore();
export default store;
